<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Upload Documents</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="goTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="goTo('employee-documents-view')">My Documents</a></li>
                    <li class="breadcrumb-item active">Upload</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading...</p>
          </div>

          <div v-else>
            <!-- Storage Stats -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                          <i class="mdi mdi-file-document-multiple"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Total Documents</p>
                        <h4 class="mb-0">{{ stats.totalDocuments }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                          <i class="mdi mdi-check-circle"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Verified</p>
                        <h4 class="mb-0">{{ stats.verifiedDocuments }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                          <i class="mdi mdi-clock-outline"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Pending</p>
                        <h4 class="mb-0">{{ stats.pendingDocuments }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-animate">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="avatar-sm flex-shrink-0">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                          <i class="mdi mdi-harddisk"></i>
                        </span>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1">Storage Used</p>
                        <h4 class="mb-0">{{ stats.totalSizeMB }} MB</h4>
                        <p class="text-muted mb-0 small">of {{ stats.storageLimitMB }} MB</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Storage Progress -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="mb-0">Storage Usage</h6>
                      <span class="badge" :class="getStorageBadgeClass()">
                        {{ stats.storageUsedPercent }}%
                      </span>
                    </div>
                    <div class="progress" style="height: 20px;">
                      <div
                          class="progress-bar"
                          :class="getStorageProgressClass()"
                          role="progressbar"
                          :style="{ width: stats.storageUsedPercent + '%' }"
                          :aria-valuenow="stats.storageUsedPercent"
                          aria-valuemin="0"
                          aria-valuemax="100">
                        {{ stats.totalSizeMB }} MB / {{ stats.storageLimitMB }} MB
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upload Form -->
            <div class="row">
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-upload text-primary me-2"></i>
                      Upload New Document
                    </h5>
                  </div>
                  <div class="card-body">
                    <form @submit.prevent="uploadDocument">
                      <div class="row">
                        <div class="col-md-6 mb-3">
                          <label class="form-label">Document Type *</label>
                          <select v-model="uploadForm.documentType" class="form-select" required>
                            <option value="">Select document type</option>
                            <option value="Academic Certificate">Academic Certificate</option>
                            <option value="Professional Certificate">Professional Certificate</option>
                            <option value="CV/Resume">CV/Resume</option>
                            <option value="ID">National ID</option>
                            <option value="Passport">Passport</option>
                            <option value="Contract">Employment Contract</option>
                            <option value="Letter">Letter</option>
                            <option value="Medical">Medical Document</option>
                            <option value="Tax">Tax Document</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div class="col-md-6 mb-3">
                          <label class="form-label">Document Name *</label>
                          <input
                              v-model="uploadForm.documentName"
                              type="text"
                              class="form-control"
                              placeholder="E.g., Bachelor's Degree Certificate"
                              required
                          >
                        </div>

                        <div class="col-md-6 mb-3">
                          <label class="form-label">Document Number (Optional)</label>
                          <input
                              v-model="uploadForm.documentNumber"
                              type="text"
                              class="form-control"
                              placeholder="E.g., Certificate #12345"
                          >
                        </div>

                        <div class="col-md-6 mb-3">
                          <label class="form-label">Valid Until (Optional)</label>
                          <input
                              v-model="uploadForm.validTill"
                              type="date"
                              class="form-control"
                          >
                        </div>

                        <div class="col-12 mb-3">
                          <label class="form-label">Select File *</label>
                          <div class="upload-area" :class="{ 'drag-over': isDragOver }"
                               @drop.prevent="handleDrop"
                               @dragover.prevent="isDragOver = true"
                               @dragleave="isDragOver = false">
                            <input
                                ref="fileInput"
                                type="file"
                                @change="handleFileSelect"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                style="display: none"
                            >

                            <div v-if="!uploadForm.file" class="text-center p-4" @click="$refs.fileInput.click()">
                              <i class="mdi mdi-cloud-upload font-size-48 text-primary mb-3"></i>
                              <h5>Drop file here or click to browse</h5>
                              <p class="text-muted mb-0">Supported formats: PDF, JPG, PNG, DOC, DOCX (Max: 5MB)</p>
                            </div>

                            <div v-else class="selected-file p-4">
                              <div class="d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center">
                                  <i :class="getFileIcon(uploadForm.file.type)" class="font-size-36 me-3" :style="{ color: getFileColor(uploadForm.file.type) }"></i>
                                  <div>
                                    <h6 class="mb-1">{{ uploadForm.file.name }}</h6>
                                    <p class="text-muted mb-0 small">{{ formatFileSize(uploadForm.file.size) }}</p>
                                  </div>
                                </div>
                                <button type="button" @click="removeFile" class="btn btn-sm btn-danger">
                                  <i class="mdi mdi-close"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-12">
                          <div class="alert alert-info">
                            <i class="mdi mdi-information me-2"></i>
                            <strong>Important:</strong> Uploaded documents will be reviewed by HR before verification. Ensure all information is accurate and files are clear and legible.
                          </div>
                        </div>

                        <div class="col-12 d-flex justify-content-end gap-2">
                          <button type="button" @click="resetForm" class="btn btn-secondary">
                            <i class="mdi mdi-refresh me-1"></i>
                            Reset
                          </button>
                          <button type="submit" class="btn btn-primary" :disabled="uploading || !stats.canUpload">
                            <span v-if="uploading" class="spinner-border spinner-border-sm me-1"></span>
                            <i v-else class="mdi mdi-upload me-1"></i>
                            {{ uploading ? 'Uploading...' : 'Upload Document' }}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              <!-- Recent Uploads -->
              <div class="col-lg-4">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-history text-primary me-2"></i>
                      Recent Uploads
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="recentUploads.length > 0" class="timeline">
                      <div v-for="(upload, index) in recentUploads" :key="index" class="timeline-item mb-3">
                        <div class="d-flex">
                          <div class="timeline-icon">
                            <i :class="getFileIcon(getFileTypeFromName(upload.name))" class="font-size-20"></i>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1">{{ upload.name }}</h6>
                            <p class="text-muted mb-1 small">
                              <span class="badge bg-primary-subtle text-primary">{{ upload.documentType }}</span>
                            </p>
                            <p class="text-muted mb-0 small">
                              <i class="mdi mdi-clock-outline me-1"></i>
                              {{ upload.upload_date_formatted }}
                            </p>
                            <p class="mb-0 small">
                              <span v-if="upload.isVerified === '1'" class="badge bg-success">
                                <i class="mdi mdi-check"></i> Verified
                              </span>
                              <span v-else class="badge bg-warning">
                                <i class="mdi mdi-clock"></i> Pending
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                      <i class="mdi mdi-file-document-outline font-size-48"></i>
                      <p class="mt-2 mb-0">No recent uploads</p>
                    </div>
                  </div>
                </div>

                <!-- Quick Actions -->
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-lightning-bolt text-primary me-2"></i>
                      Quick Actions
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="d-grid gap-2">
                      <button @click="goTo('employee-documents-view')" class="btn btn-outline-primary">
                        <i class="mdi mdi-file-document-multiple me-2"></i>
                        View All Documents
                      </button>
                      <button @click="goTo('employee-documents-view')" class="btn btn-outline-info">
                        <i class="mdi mdi-help-circle me-2"></i>
                        Request Document from HR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeDocumentsUpload",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      uploading: false,
      isDragOver: false,
      stats: {
        totalDocuments: 0,
        verifiedDocuments: 0,
        pendingDocuments: 0,
        totalSizeMB: 0,
        storageLimitMB: 50,
        storageUsedPercent: 0,
        canUpload: true
      },
      uploadForm: {
        documentType: '',
        documentName: '',
        documentNumber: '',
        validTill: '',
        file: null
      },
      recentUploads: []
    };
  },

  mounted() {
    this.loadStats();
    this.loadRecentUploads();
  },

  methods: {
    async loadStats() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeUploadStats");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.stats = res.data.data;
        }
      } catch (error) {
        console.error("Failed to load stats:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadRecentUploads() {
      try {
        const data = new FormData();
        data.append("function", "getRecentUploads");
        data.append("limit", "5");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.recentUploads = res.data.data.recentUploads;
        }
      } catch (error) {
        console.error("Failed to load recent uploads:", error);
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    handleDrop(event) {
      this.isDragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    validateAndSetFile(file) {
      // Check file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$toast?.error("File size must be less than 5MB") ||
        alert("File size must be less than 5MB");
        return;
      }

      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      if (!allowedTypes.includes(file.type)) {
        this.$toast?.error("Only PDF, JPG, PNG, DOC, and DOCX files are allowed") ||
        alert("Only PDF, JPG, PNG, DOC, and DOCX files are allowed");
        return;
      }

      this.uploadForm.file = file;
    },

    removeFile() {
      this.uploadForm.file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async uploadDocument() {
      if (!this.uploadForm.file) {
        this.$toast?.error("Please select a file") ||
        alert("Please select a file");
        return;
      }

      if (!this.stats.canUpload) {
        this.$toast?.error("Storage limit exceeded. Please delete some documents first.") ||
        alert("Storage limit exceeded");
        return;
      }

      this.uploading = true;

      try {
        const formData = new FormData();
        formData.append("function", "uploadEmployeeDocument");
        formData.append("file", this.uploadForm.file);
        formData.append("documentType", this.uploadForm.documentType);
        formData.append("documentName", this.uploadForm.documentName);
        formData.append("documentNumber", this.uploadForm.documentNumber);
        formData.append("validTill", this.uploadForm.validTill);

        const res = await execute(formData, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Document uploaded successfully!") ||
          alert("Document uploaded successfully!");

          // Reset form
          this.resetForm();

          // Reload data
          this.loadStats();
          this.loadRecentUploads();
        } else {
          throw new Error(res.data?.message || 'Upload failed');
        }
      } catch (error) {
        this.$toast?.error("Upload failed: " + error.message) ||
        alert("Upload failed: " + error.message);
      } finally {
        this.uploading = false;
      }
    },

    resetForm() {
      this.uploadForm = {
        documentType: '',
        documentName: '',
        documentNumber: '',
        validTill: '',
        file: null
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    getStorageBadgeClass() {
      const percent = this.stats.storageUsedPercent;
      if (percent >= 90) return 'bg-danger';
      if (percent >= 70) return 'bg-warning';
      return 'bg-success';
    },

    getStorageProgressClass() {
      const percent = this.stats.storageUsedPercent;
      if (percent >= 90) return 'bg-danger';
      if (percent >= 70) return 'bg-warning';
      return 'bg-success';
    },

    getFileIcon(fileType) {
      const type = fileType?.toLowerCase() || '';

      if (type.includes('pdf')) return 'mdi mdi-file-pdf-box';
      if (type.includes('doc')) return 'mdi mdi-file-word';
      if (type.includes('xls') || type.includes('sheet')) return 'mdi mdi-file-excel';
      if (type.includes('image') || type.includes('jpg') || type.includes('png')) return 'mdi mdi-file-image';

      return 'mdi mdi-file-document';
    },

    getFileColor(fileType) {
      const type = fileType?.toLowerCase() || '';

      if (type.includes('pdf')) return '#d32f2f';
      if (type.includes('doc')) return '#2196f3';
      if (type.includes('image')) return '#9c27b0';

      return '#607d8b';
    },

    getFileTypeFromName(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'application/pdf';
      if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
      if (ext === 'png') return 'image/png';
      if (ext === 'doc') return 'application/msword';
      if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      return 'application/octet-stream';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';

      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));

      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
    },

    goTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.font-size-48 {
  font-size: 48px;
}

.font-size-36 {
  font-size: 36px;
}

.font-size-20 {
  font-size: 20px;
}

.card-animate {
  transition: all 0.3s ease;
}

.card-animate:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.03);
}

.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  background-color: #f8f9fa;
}

.upload-area:hover {
  border-color: #5b73e8;
  background-color: #f0f2ff;
}

.upload-area.drag-over {
  border-color: #5b73e8;
  background-color: #e8ebff;
}

.selected-file {
  background-color: #fff;
}

.timeline-item {
  position: relative;
  padding-left: 40px;
  border-left: 2px solid #e9ecef;
}

.timeline-icon {
  position: absolute;
  left: -12px;
  width: 24px;
  height: 24px;
  background-color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e9ecef;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }
}
</style>